import { ListOutput, PageableInput } from '@allocab/allocab-commons-shared-types';
import { VehicleWithShortDriversOutput } from '@allocab/allocab-driver-shared-types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '../services/datastore/queryKeys';
import { DEFAULT_CACHE_TIME, queryClient } from '../services/datastore/react-query';
import { InfiniteQueryOptions } from '../services/datastore/types';
import { driverApi } from '../services/network/Api';

export const VEHICLES_LIMIT = 10;

export type Input = Omit<PageableInput, 'offset' | 'limit'> & { limit?: number };

export const fetchVehicles = (input: PageableInput) =>
  driverApi.get<PageableInput, VehicleWithShortDriversOutput, 'list'>('/v1/vehicle/vehicles/by-account', input);

export const useFethVehiclesPage = (input: Input, pageNumber = 1) => {
  const limit = input.limit ?? VEHICLES_LIMIT;
  const apiInput: PageableInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  return useQuery(queryKeys.user.fetchVehicles(apiInput), () => fetchVehicles(apiInput), {
    keepPreviousData: true,
  });
};

export const prefetchVehiclesPage = (input: Input, pageNumber = 1) => {
  const limit = input.limit ?? VEHICLES_LIMIT;
  const apiInput: PageableInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  queryClient.prefetchQuery(queryKeys.user.fetchVehicles(apiInput), () => fetchVehicles(apiInput), {
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const useInfiniteFetchVehicles = (
  input: Input,
  options: InfiniteQueryOptions<
    typeof queryKeys.user.fetchInfiniteVehicles,
    ListOutput<VehicleWithShortDriversOutput>
  > = {},
) => {
  const fetchVehiclesQuery = useInfiniteQuery(
    queryKeys.user.fetchInfiniteVehicles(input),
    ({ pageParam = 0 }: { pageParam?: number }) =>
      fetchVehicles({ limit: VEHICLES_LIMIT, offset: pageParam, orderBy: 'vehicle.validated', ...input }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.nbItems < VEHICLES_LIMIT ? undefined : VEHICLES_LIMIT * allPages.length,
      ...options,
    },
  );

  return fetchVehiclesQuery;
};
